import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import PageLayout from '../../components/layouts/PageLayout';
import CriticalEscalationTab from '../../components/escalation/CriticalEscalationTab';
import AppointmentReminderSection from '../../components/appointmentReminder/AppointmentReminderSection';
import PatientLinkTimeSection from '../../components/patientLinkTime/PatientLinkTimeSection';
import NotificationRetrySection from '../../components/notificationRetry/NotificationRetrySection';
import PeerReviewSection from '../../components/peerReviewConfig/PeerReviewSection';
import RoutingRulesSection from '../../components/routingRules/RoutingRulesSection';
import StudyAssignmentSection from '../../components/studyAssignment/StudyAssignmentSection';
import WorklistPrioritySection from '../../components/worklistPriority/WorklistPrioritySection';
import { authService } from '../../auth/authService';
import { canAccessPage, isSpokeFacilityUser } from '../../utils/permissions';
import { getDefaultRoute } from '../../utils/getDefaultRoute';
import { RIS_CONFIG_TABS } from '../../constants/escalationContact.constants';
import './RisConfiguration.css';
import './ris-config-responsive.css';

const VALID_TAB_IDS = new Set(RIS_CONFIG_TABS.map(tab => tab.id));

function resolveInitialTab(tabParam: string | null, user: ReturnType<typeof authService.getUser>) {
  if (tabParam && VALID_TAB_IDS.has(tabParam as (typeof RIS_CONFIG_TABS)[number]['id'])) {
    if (tabParam === 'routing' && !isSpokeFacilityUser(user)) {
      return 'worklist';
    }
    return tabParam;
  }
  return isSpokeFacilityUser(user) ? 'routing' : 'worklist';
}

export default function RisConfiguration() {
  const user = authService.getUser();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() =>
    resolveInitialTab(searchParams.get('tab'), user)
  );
  const [contactDrawerOpen, setContactDrawerOpen] = useState(false);
  const contactDrawerSlotRef = useRef<HTMLDivElement>(null);

  const facilityId = useMemo(() => {
    const stored = authService.getFacilityId();
    const fromUser = user?.facility_id;
    const parsed = Number(stored || fromUser);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }, [user]);

  const visibleTabs = useMemo(
    () =>
      RIS_CONFIG_TABS.filter(tab => {
        if (tab.id === 'routing') {
          return isSpokeFacilityUser(user);
        }
        return !tab.disabled;
      }),
    [user]
  );

  useEffect(() => {
    if (!visibleTabs.some(tab => tab.id === activeTab)) {
      setActiveTab(visibleTabs[0]?.id ?? 'worklist');
    }
  }, [visibleTabs, activeTab]);

  if (!canAccessPage(user, '/ris-configuration')) {
    return (
      <Navigate
        to={getDefaultRoute(user)}
        replace
      />
    );
  }

  if (!facilityId) {
    return (
      <PageLayout
        title="RIS Configuration"
        breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: 'Dashboard', path: '/admin-dashboard' },
          { label: 'RIS Config' },
        ]}
      >
        <div className="ris-config-page">
          <p
            className="esc-error-banner"
            role="alert"
          >
            Your account is not associated with a facility. Contact your administrator.
          </p>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="RIS Configuration"
      hideTitleInHeader
      contentClassName={contactDrawerOpen ? 'ris-config-layout-content--drawer-open' : undefined}
      breadcrumbs={[
        { label: 'Home', path: '/' },
        { label: 'Dashboard', path: '/admin-dashboard' },
        { label: 'RIS Config' },
      ]}
    >
      <div
        className={['ris-config-page', contactDrawerOpen ? 'ris-config-page--drawer-open' : '']
          .filter(Boolean)
          .join(' ')}
      >
        <div
          className={['ris-config-shell', contactDrawerOpen ? 'ris-config-shell--drawer-open' : '']
            .filter(Boolean)
            .join(' ')}
        >
          <div className="ris-config-page-main">
            <header className="ris-config-header">
              <h1 className="ris-config-title">RIS Configuration</h1>
            </header>

            <nav
              className="ris-config-tabs"
              aria-label="RIS configuration sections"
            >
              {visibleTabs.map(tab => (
                <button
                  key={tab.id}
                  type="button"
                  className={[
                    'ris-config-tab',
                    activeTab === tab.id ? 'ris-config-tab--active' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </nav>

            {activeTab === 'routing' && <RoutingRulesSection />}

            {activeTab === 'worklist' && <WorklistPrioritySection facilityId={facilityId} />}

            {activeTab === 'assignment' && <StudyAssignmentSection facilityId={facilityId} />}

            {activeTab === 'critical-escalation' && (
              <CriticalEscalationTab
                facilityId={facilityId}
                drawerContainerRef={contactDrawerSlotRef}
                onDrawerOpenChange={setContactDrawerOpen}
              />
            )}

            {activeTab === 'peer-review' && <PeerReviewSection facilityId={facilityId} />}

            {activeTab === 'patient-access-link-validity' && (
              <PatientLinkTimeSection facilityId={facilityId} />
            )}

            {activeTab === 'notification-retry' && (
              <NotificationRetrySection facilityId={facilityId} />
            )}

            {activeTab === 'appointment-reminder' && (
              <AppointmentReminderSection facilityId={facilityId} />
            )}
          </div>

          <div
            ref={contactDrawerSlotRef}
            className="ris-config-contact-drawer-slot"
          />
        </div>
      </div>
    </PageLayout>
  );
}
